import React, { useState } from 'react'
import { Cog6ToothIcon, CreditCardIcon } from '@heroicons/react/24/outline'
import { tokenUsageAPI } from '@api/tokenUsage'
import { useQuery } from '@tanstack/react-query'
import { ChartBarIcon } from '@heroicons/react/24/outline'
import { AIProviderSelector } from '@components/common/AIProviderSelector'
import EditorSettingsPanel from '@components/settings/EditorSettingsPanel'
import { SubscriptionModal } from '@components/subscription/SubscriptionModal'
import { SubscriptionBadge } from '@components/subscription/SubscriptionBadge'
import { subscriptionsAPI } from '@api/subscriptions'

const SettingsPage: React.FC = () => {
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)

  // Fetch token usage statistics (only on mount, no auto-refetch)
  const { data: tokenStats, isLoading: isLoadingTokens } = useQuery({
    queryKey: ['token-usage', 'user-stats'],
    queryFn: () => tokenUsageAPI.getUserStats(),
    // No refetchInterval - will only refetch when invalidated after AI operations or when page is revisited
  })

  // Fetch subscription info
  const { data: subscriptionInfo, isLoading: isLoadingSubscription } = useQuery({
    queryKey: ['subscription', 'info'],
    queryFn: () => subscriptionsAPI.getInfo(),
  })

  return (
    <div className="h-full bg-gray-50 dark:bg-gray-900 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex items-center">
              <Cog6ToothIcon className="w-6 h-6 text-gray-600 dark:text-gray-400 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
            </div>
          </div>

          {/* Settings Content */}
          <div className="px-6 py-6 space-y-8">
            {/* Document Settings */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Document Settings
              </h2>
              
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-900 dark:text-white">
                      Auto-Save
                    </label>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Your documents are automatically saved 2 seconds after you stop typing. 
                      No manual save required.
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center px-3 py-1.5 bg-primary-100 dark:bg-primary-900/30 rounded-full">
                      <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                        Always On
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Editor Settings */}
            <div>
              <EditorSettingsPanel />
            </div>

            {/* AI Provider Preferences */}
            <div>
              <AIProviderSelector />
            </div>

            {/* Subscription Management */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <CreditCardIcon className="w-5 h-5 mr-2" />
                Subscription
              </h2>
              
              {isLoadingSubscription ? (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  Loading subscription info...
                </div>
              ) : subscriptionInfo ? (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Plan</p>
                      <div className="flex items-center gap-2">
                        <SubscriptionBadge
                          subscriptionType={subscriptionInfo.subscription_type}
                          planName={subscriptionInfo.plan_name}
                          isActive={subscriptionInfo.is_active}
                          size="md"
                        />
                        {subscriptionInfo.next_billing_date && (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Renews: {new Date(subscriptionInfo.next_billing_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setShowSubscriptionModal(true)}
                      className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors"
                    >
                      {subscriptionInfo.subscription_type === 'free' ? 'Subscribe' : 'Manage'}
                    </button>
                  </div>
                  
                  {subscriptionInfo.subscription_type !== 'free' && (
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Plan Benefits</p>
                      <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        <li>• {subscriptionInfo.monthly_credits.toLocaleString()} credits per month</li>
                        {subscriptionInfo.features && Object.entries(subscriptionInfo.features).map(([key, value]) => (
                          <li key={key}>
                            • {key.replace(/_/g, ' ')}: {value === true ? 'Enabled' : value === false ? 'Disabled' : value}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  Unable to load subscription information.
                </div>
              )}
            </div>

            {/* Token Usage Statistics */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <ChartBarIcon className="w-5 h-5 mr-2" />
                Token Usage
              </h2>
              
              {isLoadingTokens ? (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  Loading token usage...
                </div>
              ) : tokenStats ? (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Tokens</div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {tokenStats.total_tokens.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Input Tokens</div>
                      <div className="text-xl font-semibold text-blue-600 dark:text-blue-400">
                        {tokenStats.total_input_tokens.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Output Tokens</div>
                      <div className="text-xl font-semibold text-purple-600 dark:text-purple-400">
                        {tokenStats.total_output_tokens.toLocaleString()}
                      </div>
                    </div>
                    {tokenStats.total_cost_usd && (
                      <div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Estimated Cost</div>
                        <div className="text-xl font-semibold text-green-600 dark:text-green-400">
                          ${tokenStats.total_cost_usd.toFixed(4)}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {Object.keys(tokenStats.operation_counts).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Usage by Operation Type:
                      </div>
                      <div className="space-y-2">
                        {Object.entries(tokenStats.operation_counts).map(([operation, count]) => (
                          <div key={operation} className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400 capitalize">
                              {operation.replace('_', ' ')}
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {count} calls
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No token usage data available yet.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        currentSubscription={subscriptionInfo}
      />
    </div>
  )
}

export default SettingsPage
